import { useParams, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DashboardLayout from "../layouts/dashboard";
import { StaggerContainer, StaggerItem, FadeIn } from "../../interface/shared/animations";
import SearchField from "../../interface/shared/searchfield";
import { ExpiresAtDisplay } from "../../interface/shared/expirydisplay";
import { WeightDisplay } from "../../interface/shared/weightdisplay";
import { useStockLocationDetail } from "../../api/stocklocations";
import { StockLocation } from "../../shared/types";

export default function StockLocationDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {
        stockLocation, errors, loading, fetchStockLocation,
        stockItems, stockErrors, stockLoading, fetchStockItems,
        page, perPage, total, setPage, setPerPage,
        searchTerm, setSearchTerm, sortField, sortOrder, setSort,
    } = useStockLocationDetail(id);

    return (
        <DashboardLayout>
            <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackRoundedIcon />}
                        onClick={() => navigate("/locations")}
                    >
                        Back
                    </Button>
                </Stack>

                {errors.length > 0 && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        action={
                            <Button color="inherit" size="small" onClick={fetchStockLocation}>
                                Retry
                            </Button>
                        }
                    >
                        {errors.join(" ")}
                    </Alert>
                )}

                {loading ? <_DetailsSkeleton /> : stockLocation && (
                    <StaggerContainer>
                        <StaggerItem>
                            <_LocationSummary location={stockLocation} />
                        </StaggerItem>

                        <StaggerItem>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                justifyContent="space-between"
                                sx={{ mt: 4, mb: 2 }}
                            >
                                <Typography variant="h6" fontWeight={600}>
                                    Stock ({total})
                                </Typography>
                                <SearchField
                                    placeholder="Search stock by product code or name"
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                />
                            </Stack>

                            {stockErrors.length > 0 && (
                                <Alert
                                    severity="error"
                                    sx={{ mb: 2 }}
                                    action={
                                        <Button color="inherit" size="small" onClick={() => fetchStockItems()}>
                                            Retry
                                        </Button>
                                    }
                                >
                                    {stockErrors.join(" ")}
                                </Alert>
                            )}

                            <Card variant="outlined">
                                <CardContent>
                                    {stockLoading ? (
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Code</TableCell>
                                                    <TableCell>Product</TableCell>
                                                    <TableCell>Level</TableCell>
                                                    <TableCell>Quantity</TableCell>
                                                    <TableCell>Weight</TableCell>
                                                    <TableCell>Expires At</TableCell>
                                                    <TableCell align="right"></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Array.from({ length: 5 }).map((_, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell><Skeleton variant="text" /></TableCell>
                                                        <TableCell><Skeleton variant="text" /></TableCell>
                                                        <TableCell><Skeleton variant="text" /></TableCell>
                                                        <TableCell><Skeleton variant="text" /></TableCell>
                                                        <TableCell><Skeleton variant="text" /></TableCell>
                                                        <TableCell><Skeleton variant="text" /></TableCell>
                                                        <TableCell align="right">
                                                            <Box alignItems="center" justifyContent="end" display="flex">
                                                                <Skeleton variant="rounded" width={70} height={30} />
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <FadeIn>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Code</TableCell>
                                                        <TableCell>Product</TableCell>
                                                        <TableCell>Level</TableCell>
                                                        <TableCell sortDirection={sortField === "quantity" ? sortOrder : false}>
                                                            <TableSortLabel
                                                                active={sortField === "quantity"}
                                                                direction={sortField === "quantity" ? sortOrder : "asc"}
                                                                onClick={() => setSort("quantity")}
                                                            >
                                                                Quantity
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell sortDirection={sortField === "weight" ? sortOrder : false}>
                                                            <TableSortLabel
                                                                active={sortField === "weight"}
                                                                direction={sortField === "weight" ? sortOrder : "asc"}
                                                                onClick={() => setSort("weight")}
                                                            >
                                                                Weight
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell sortDirection={sortField === "expires_at" ? sortOrder : false}>
                                                            <TableSortLabel
                                                                active={sortField === "expires_at"}
                                                                direction={sortField === "expires_at" ? sortOrder : "asc"}
                                                                onClick={() => setSort("expires_at")}
                                                            >
                                                                Expires At
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell align="right"></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {stockItems.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={7}>
                                                                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                                                    No stock found at this location.
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : stockItems.map((item) => (
                                                        <TableRow key={item.id}>
                                                            <TableCell>{item.product.code}</TableCell>
                                                            <TableCell>{item.product.name}</TableCell>
                                                            <TableCell>{item.level}</TableCell>
                                                            <TableCell>{item.quantity}</TableCell>
                                                            <TableCell><WeightDisplay value={item.weight} /></TableCell>
                                                            <TableCell><ExpiresAtDisplay value={item.expires_at} variant="simple" /></TableCell>
                                                            <TableCell align="right">
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                                    onClick={() => navigate(`/stock/${item.id}`)}
                                                                >
                                                                    View
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                            <TablePagination
                                                component="div"
                                                count={total}
                                                page={page - 1}
                                                onPageChange={(_, newPage) => setPage(newPage + 1)}
                                                rowsPerPage={perPage}
                                                onRowsPerPageChange={(e) => {
                                                    setPerPage(parseInt(e.target.value, 10));
                                                    setPage(1);
                                                }}
                                                rowsPerPageOptions={[10, 15, 25, 50]}
                                            />
                                        </FadeIn>
                                    )}
                                </CardContent>
                            </Card>
                        </StaggerItem>
                    </StaggerContainer>
                )}
            </Box>
        </DashboardLayout>
    );
}

function _LocationSummary({ location }: { location: StockLocation }) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h6">
                            {location.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {location.warehouse.name}
                        </Typography>
                    </Box>
                </Stack>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary">Location</Typography>
                        <Typography variant="body1" fontWeight={500}>{location.name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary">Warehouse</Typography>
                        <Typography variant="body1" fontWeight={500}>{location.warehouse.name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary">Levels</Typography>
                        <Typography variant="body1" fontWeight={500}>{location.levels}</Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function _DetailsSkeleton() {
    return (
        <>
            <Card variant="outlined">
                <CardContent>
                    <Skeleton variant="text" width={200} height={32} />
                    <Skeleton variant="text" width={150} height={20} sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 6, sm: 3 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="text" width={120} height={24} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
            <Skeleton variant="text" width={120} height={28} sx={{ mt: 4, mb: 2 }} />
            <Card variant="outlined">
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Code</TableCell>
                                <TableCell>Product</TableCell>
                                <TableCell>Level</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Weight</TableCell>
                                <TableCell>Expires At</TableCell>
                                <TableCell align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell align="right">
                                        <Box alignItems="center" justifyContent="end" display="flex">
                                            <Skeleton variant="rounded" width={70} height={30} />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </>
    );
}
